import type { IUsers } from "@infra/interfaces/auth/IUsers";
import { type Result, ok, err } from "@domain/shared/result/Result";

export type ValidateTokenError = "invalid_token" | "expired_token";
export type SetPasswordError = "token_no_longer_valid";

export interface PasswordResetUseCaseDeps {
	userApi: IUsers;
}

/**
 * Three-step password reset flow:
 *  1. `requestReset(email)` — emails a reset link.
 *  2. `validateToken(token)` — when the user follows the link, we confirm
 *     the token is good and recover the email it was issued to.
 *  3. `setNewPassword(email, password, token)` — commits the new password.
 *
 * Bundled together because they share the `IUsers` dependency and form a
 * single conceptual flow.
 */
export class PasswordResetUseCase {
	constructor(private readonly deps: PasswordResetUseCaseDeps) {}

	async requestReset(email: string): Promise<void> {
		await this.deps.userApi.requestPasswordReset(email);
	}

	async validateToken(token: string): Promise<Result<{ email: string }, ValidateTokenError>> {
		if (!token) return err("invalid_token");
		const email = await this.deps.userApi.validateResetToken(token);
		if (!email) return err("invalid_token");
		return ok({ email });
	}

	async setNewPassword(email: string, password: string, token: string): Promise<Result<void, SetPasswordError>> {
		const success = await this.deps.userApi.setNewPassword(email, password, token);
		if (!success) return err("token_no_longer_valid");
		return ok(undefined);
	}
}
